import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { KeywordAlertConfigurationCreateRequest, ThresholdAlertConfigurationCreateRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useCreateNumberOfLikesAlert = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: ThresholdAlertConfigurationCreateRequest) =>
      api.postAlertsConfigurationsNumberOfLikesV1(payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.alerts.create.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.alerts.create.error") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ALERTS_LIST() });
    },
  });
};

const useCreateNumberOfCommentsAlert = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: ThresholdAlertConfigurationCreateRequest) =>
      api.postAlertsConfigurationsNumberOfCommentsV1(payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.alerts.create.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.alerts.create.error") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ALERTS_LIST() });
    },
  });
};

const useCreatePostKeywordsAlert = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: KeywordAlertConfigurationCreateRequest) =>
      api.postAlertsConfigurationsPostKeywordsV1(payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.alerts.create.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.alerts.create.error") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ALERTS_LIST() });
    },
  });
};

const useCreateCommentKeywordsAlert = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: KeywordAlertConfigurationCreateRequest) =>
      api.postAlertsConfigurationsCommentKeywordsV1(payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.alerts.create.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.alerts.create.error") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ALERTS_LIST() });
    },
  });
};

const useEditNumberOfLikesAlert = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ThresholdAlertConfigurationCreateRequest }) =>
      api.putAlertsConfigurationsNumberOfLikesV1(id, payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.alerts.edit.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.alerts.edit.error") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ALERTS_LIST() });
    },
  });
};

const useEditNumberOfCommentsAlert = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ThresholdAlertConfigurationCreateRequest }) =>
      api.putAlertsConfigurationsNumberOfCommentsV1(id, payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.alerts.edit.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.alerts.edit.error") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ALERTS_LIST() });
    },
  });
};

const useEditPostKeywordsAlert = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: KeywordAlertConfigurationCreateRequest }) =>
      api.putAlertsConfigurationsPostKeywordsV1(id, payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.alerts.edit.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.alerts.edit.error") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ALERTS_LIST() });
    },
  });
};

const useEditCommentKeywordsAlert = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: KeywordAlertConfigurationCreateRequest }) =>
      api.putAlertsConfigurationsCommentKeywordsV1(id, payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.alerts.edit.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.alerts.edit.error") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ALERTS_LIST() });
    },
  });
};

export const alertsMutations = {
  useCreateNumberOfLikesAlert,
  useCreateNumberOfCommentsAlert,
  useCreatePostKeywordsAlert,
  useCreateCommentKeywordsAlert,
  useEditNumberOfLikesAlert,
  useEditNumberOfCommentsAlert,
  useEditPostKeywordsAlert,
  useEditCommentKeywordsAlert,
};
